import React from 'react'

const POLL_INTERVAL_MS = 15_000

export type WorkflowState = 'idle' | 'running' | 'completed' | 'failed'

export interface WorkflowStatusResult {
  status: string
}

/**
 * Reusable hook for polling workflow status.
 *
 * @param fetchStatus — function that fetches the current workflow status
 *   by workflow ID. Injected to avoid coupling shared/ to components/.
 *
 * Usage:
 *   const { workflowStatus, lastChecked, startPolling, reset } =
 *     useWorkflowStatus(getWorkflowStatus)
 *   startPolling(workflowId, { onCompleted: () => ... })
 */
export function useWorkflowStatus(
  fetchStatus: (workflowId: string) => Promise<WorkflowStatusResult>,
) {
  const [workflowStatus, setWorkflowStatus] =
    React.useState<WorkflowState>('idle')
  const [lastChecked, setLastChecked] = React.useState<Date | null>(null)
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const completedRef = React.useRef(false)
  const mountedRef = React.useRef(true)

  React.useEffect(() => {
    return () => {
      mountedRef.current = false
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [])

  function startPolling(
    workflowId: string,
    callbacks?: { onCompleted?: () => void; onFailed?: () => void },
  ) {
    // Clear any existing polling interval to prevent leaks.
    if (pollRef.current) clearInterval(pollRef.current)
    setWorkflowStatus('running')
    setLastChecked(null)
    completedRef.current = false

    const poll = async () => {
      if (completedRef.current || !mountedRef.current) return
      try {
        const status = await fetchStatus(workflowId)
        if (completedRef.current || !mountedRef.current) return
        setLastChecked(new Date())
        if (status?.status?.includes('COMPLETED')) {
          completedRef.current = true
          setWorkflowStatus('completed')
          if (pollRef.current) clearInterval(pollRef.current)
          callbacks?.onCompleted?.()
        } else if (
          status?.status?.includes('FAILED') ||
          status?.status?.includes('CANCELED') ||
          status?.status?.includes('TERMINATED') ||
          status?.status?.includes('TIMED_OUT')
        ) {
          completedRef.current = true
          setWorkflowStatus('failed')
          if (pollRef.current) clearInterval(pollRef.current)
          callbacks?.onFailed?.()
        }
      } catch {
        // Network error during polling — keep trying.
        if (mountedRef.current) {
          setLastChecked(new Date())
        }
      }
    }

    poll()
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS)
  }

  function reset() {
    setWorkflowStatus('idle')
    setLastChecked(null)
    completedRef.current = false
    if (pollRef.current) clearInterval(pollRef.current)
  }

  return { workflowStatus, lastChecked, startPolling, reset }
}
